import type { LifestyleImage } from "./useLifestyleImages";
import { inkVariantFor } from "@/utils/merchDesigns";
import { GRID_MOCKUP_WIDTH } from "@/utils/merchMockups";
import { itemMockupUrl, itemName, type StoreListItem } from "@/utils/merchStore";

export const MAX_CARD_SLIDES = 6;

export const MAX_CARD_COLOUR_MOCKUPS = 2;

export interface CardSlide {
  kind: "mockup" | "lifestyle" | "print";
  key: string;
  url: string;
  alt: string;
}

export function cloudNameFromUrl(url: string | null | undefined): string | null {
  const match = url?.match(/^https:\/\/res\.cloudinary\.com\/([^/]+)\//);
  return match ? match[1] : null;
}

export function printPreviewUrl(cloudName: string, publicId: string): string {
  return `https://res.cloudinary.com/${cloudName}/image/upload/c_limit,w_900,f_auto,q_auto/${publicId}`;
}

export function cardSlides(item: StoreListItem, lifestyle: LifestyleImage[]): CardSlide[] {
  const name = itemName(item);
  const chosen = item.colors.find((color) => color.name === item.defaultColor) ?? null;
  const slides: CardSlide[] = [];
  const seen = new Set<string>();
  const push = (slide: CardSlide) => {
    if (!slide.url || seen.has(slide.url)) return;
    seen.add(slide.url);
    slides.push(slide);
  };

  if (item.mockupUrl) push({ kind: "mockup", key: `mockup:${item.defaultColor}`, url: item.mockupUrl, alt: `${name} in ${chosen?.label ?? item.defaultColor}` });

  for (const image of lifestyle) {
    push({ kind: "lifestyle", key: image.url, url: image.url, alt: `${name} worn, ${image.shot} view` });
  }

  const cloudName = cloudNameFromUrl(item.mockupUrl);
  if (cloudName && chosen) {
    const ink = inkVariantFor(item.inks, chosen.isDark);
    if (ink?.cloudinaryPublicId) {
      push({ kind: "print", key: `print:${ink.cloudinaryPublicId}`, url: printPreviewUrl(cloudName, ink.cloudinaryPublicId), alt: `The ${item.designTitle} print on its own` });
    }

    const others = item.colors
      .filter((color) => color.name !== item.defaultColor)
      .sort((left, right) => Number(left.isDark === chosen.isDark) - Number(right.isDark === chosen.isDark));
    let added = 0;
    for (const color of others) {
      if (added >= MAX_CARD_COLOUR_MOCKUPS) break;
      const url = itemMockupUrl(item, cloudName, GRID_MOCKUP_WIDTH, color.name);
      if (!url) continue;
      push({ kind: "mockup", key: `mockup:${color.name}`, url, alt: `${name} in ${color.label}` });
      added += 1;
    }
  }

  return slides.slice(0, MAX_CARD_SLIDES);
}
